import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

import type { CrmDataProvider } from "../providers/types";

type SyncResult = Awaited<ReturnType<CrmDataProvider["syncFromTripletex"]>>;

// Synk-dialogen kjører alltid en tørrkjøring («Sjekk match») først: samme
// matching som den ekte synken, men uten skriving. Rapporten viser hvilke
// Tripletex-kunder som IKKE matcher og dermed ville blitt opprettet som
// tvillinger – rett kundenummeret på riktig Mimin-kunde og sjekk på nytt,
// før synken faktisk kjøres.
export const TripletexSyncDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const [preview, setPreview] = useState<SyncResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const loadPreview = async () => {
    setIsLoading(true);
    setPreview(null);
    setPreviewError(null);
    try {
      setPreview(await dataProvider.syncFromTripletex(true));
    } catch (error) {
      setPreviewError(
        error instanceof Error
          ? error.message
          : "Kunne ikke kjøre tørrkjøringen mot Tripletex",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const runSync = async () => {
    setIsSyncing(true);
    try {
      const result = await dataProvider.syncFromTripletex();
      notify("crm.tripletex_sync.done", {
        type: "info",
        messageArgs: {
          _: `Synk fullført. Kunder: ${result.customers.created} nye, ${result.customers.updated} oppdatert. Kontakter: ${result.contacts.created} nye, ${result.contacts.updated} oppdatert. Produkter: ${result.products.created} nye, ${result.products.updated} oppdatert, ${result.products.deactivated} deaktivert.`,
        },
      });
      queryClient.invalidateQueries();
      onClose();
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : "Kunne ikke synkronisere fra Tripletex",
        { type: "error" },
      );
    } finally {
      setIsSyncing(false);
    }
  };

  const NameList = ({ names }: { names: string[] }) =>
    names.length === 0 ? null : (
      <ul className="mt-1 max-h-40 overflow-y-auto rounded-md border px-3 py-1.5 text-xs">
        {names.map((name) => (
          <li key={name}>{name}</li>
        ))}
      </ul>
    );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="lg:max-w-xl overflow-y-auto max-h-9/10">
        <DialogHeader>
          <DialogTitle>
            {translate("crm.tripletex_sync.title", {
              _: "Synk fra Tripletex",
            })}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {translate("crm.tripletex_sync.content", {
            _: "Tripletex er master for stamdata: navn, kundenummer, adresser og priser overskrives i Mimin. Notater, oppfølging og produktkoblinger per kunde røres ikke.",
          })}
        </p>

        {isLoading && <Skeleton className="h-32 w-full" />}

        {previewError && (
          <p className="text-sm text-destructive">{previewError}</p>
        )}

        {preview && (
          <div className="space-y-3 text-sm">
            <div>
              <span className="font-medium">
                {translate("crm.tripletex_sync.preview_customers", {
                  _: "Kunder",
                })}
                :
              </span>{" "}
              {translate("crm.tripletex_sync.preview_customer_counts", {
                matched: preview.customers.updated,
                created: preview.customers.created,
                _: `${preview.customers.updated} matcher, ${preview.customers.created} vil bli opprettet som nye.`,
              })}
              {preview.customers.toCreate.length > 0 && (
                <>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {translate("crm.tripletex_sync.preview_twin_hint", {
                      _: "Skal noen av disse matche en eksisterende Mimin-kunde? Sett kundenummeret på Mimin-kunden og sjekk på nytt – ellers opprettes de som nye kort.",
                    })}
                  </p>
                  <NameList names={preview.customers.toCreate} />
                </>
              )}
            </div>
            <div>
              <span className="font-medium">
                {translate("crm.tripletex_sync.preview_products", {
                  _: "Produkter",
                })}
                :
              </span>{" "}
              {translate("crm.tripletex_sync.preview_product_counts", {
                matched: preview.products.updated,
                created: preview.products.created,
                deactivated: preview.products.deactivated,
                _: `${preview.products.updated} matcher, ${preview.products.created} nye, ${preview.products.deactivated} deaktiveres.`,
              })}
              <NameList
                names={[
                  ...preview.products.toCreate.map((name) => `+ ${name}`),
                  ...preview.products.toDeactivate.map((name) => `− ${name}`),
                ]}
              />
            </div>
            <div>
              <span className="font-medium">
                {translate("crm.tripletex_sync.preview_contacts", {
                  _: "Kontakter",
                })}
                :
              </span>{" "}
              {translate("crm.tripletex_sync.preview_contact_counts", {
                matched: preview.contacts.updated,
                created: preview.contacts.created,
                skipped: preview.contacts.skipped,
                _: `${preview.contacts.updated} matcher, ${preview.contacts.created} nye, ${preview.contacts.skipped} uten e-post hoppes over.`,
              })}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={loadPreview}
            disabled={isLoading || isSyncing}
          >
            {translate("crm.tripletex_sync.recheck", { _: "Sjekk på nytt" })}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSyncing}>
              {translate("ra.action.cancel", { _: "Avbryt" })}
            </Button>
            <Button
              onClick={runSync}
              disabled={isLoading || isSyncing || !preview}
            >
              {isSyncing
                ? translate("crm.tripletex_sync.syncing", { _: "Synker …" })
                : translate("crm.tripletex_sync.confirm", { _: "Kjør synk" })}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
